// Grids & Gantts — columns, changed where they live.
//
// A column is changed ON THE COLUMN, not in a settings page two screens
// away. Click a heading and you get that column: what it reads, how wide,
// which way it aligns, whether it sums, and where it sits. The "+" at the
// end of the row adds one. Both are admin-only — a reader cannot rearrange
// the structure under everybody else, because these edits are saved to the
// page for everybody.
//
// It writes into the same `columns` array Page settings edits, so there is
// one config and one place it is stored; this is a second way in, not a
// second copy.

import { useEffect, useMemo, useRef, useState } from 'react';
import { FONT } from './theme';
import Sheet from '../shared/Sheet';
import { COLUMN_KINDS, newColumn } from './config';
import { FORMULA_HELP } from './grouping';
import { PSEUDO_FIELDS, WHERE, BAND_COLORS } from './columns';
import { externalSources } from '@modules/database/external-sources/frontend/api';

const NO_FIELD = ['key', 'summary', 'parent', 'status', 'type', 'count', 'countSub', 'progress', 'countIf', 'ratio', 'diff', 'divide', 'formula', 'stock'];

// "20 green, 30 blue, red" ⇄ [{ below: 20, color: 'green' }, …, { below: null, color: 'red' }]
export const bandsText = (bands) => (bands || []).map((b) => (b.below == null ? b.color : `${b.below} ${b.color}`)).join(', ');
export const parseBands = (s) => String(s || '').split(',').map((p) => p.trim()).filter(Boolean).map((p) => {
  const m = p.match(/^(-?\d+(?:\.\d+)?)\s+([a-z]+)$/i);
  return m ? { below: Number(m[1]), color: m[2].toLowerCase() } : { below: null, color: p.toLowerCase() };
}).filter((b) => BAND_COLORS.includes(b.color));
const listText = (a) => (a || []).join(', ');
const parseList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

// The optional shaping of a figure column (see ./columns.js) — which work
// types, which condition, the format, the colour bands.
function ShapeBlock({ col, cols, onChange, S, small, row }) {
  const k = col.kind;
  const figure = ['number', 'count', 'countSub', 'countIf', 'ratio', 'diff', 'divide', 'progress', 'formula', 'lookup'].includes(k);
  if (!figure) return null;
  const typed = ['number', 'countIf', 'ratio', 'progress', 'formula'].includes(k) && ((k !== 'number' && k !== 'formula') || col.agg === 'sum' || col.agg === 'count');
  const f = col.format || null;
  const setF = (patch) => onChange({ format: { prefix: '', decimals: null, thousands: ',', ...(f || {}), ...patch } });
  const others = (cols || []).filter((c) => c.id !== col.id && ['number', 'count', 'countSub', 'countIf', 'formula', 'lookup'].includes(c.kind));
  // A ratio may also divide a Difference (Budget Left ÷ Budget) — never another ratio.
  const dividends = (cols || []).filter((c) => c.id !== col.id && ['number', 'count', 'countSub', 'countIf', 'formula', 'diff', 'lookup'].includes(c.kind));
  const lab = { ...S.tiny, display: 'block', width: '100%', margin: '2px 0 1px' };
  // Text inputs keep their own draft and commit on blur, so typing a comma
  // is not parsed away mid-word.
  const Txt = ({ value, onCommit, placeholder }) => (
    <input defaultValue={value} key={value} placeholder={placeholder} onBlur={(e) => { if (e.target.value !== value) onCommit(e.target.value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={{ ...small, flex: '1 1 100%' }} />
  );
  return (
    <div style={{ borderTop: `1px dashed ${S.ui ? S.ui.border : '#ccc'}`, paddingTop: 6, marginBottom: 6 }}>
      {typed && (
        <div style={row}>
          <span style={lab}>Only these work types (blank = all)</span>
          <Txt value={listText(col.types)} placeholder="e.g. Task, Bug" onCommit={(v) => onChange({ types: parseList(v) })} />
          <span style={lab}>Never these work types</span>
          <Txt value={listText(col.notTypes)} placeholder="e.g. Sub-task" onCommit={(v) => onChange({ notTypes: parseList(v) })} />
        </div>
      )}
      {(k === 'countIf' || k === 'ratio') && (
        <div style={row}>
          <span style={lab}>{k === 'ratio' ? 'Where (the part)' : 'Count where'}</span>
          <select value={col.where} onChange={(e) => onChange({ where: e.target.value })} style={{ ...small, flex: '1 1 100%' }}>
            {WHERE.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
          </select>
          {k === 'ratio' && (<>
            <span style={lab}>Out of (the whole)</span>
            <select value={col.of} onChange={(e) => onChange({ of: e.target.value })} style={{ ...small, flex: '1 1 100%' }}>
              {WHERE.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </>)}
        </div>
      )}
      {k === 'diff' && (
        <div style={row}>
          {['a', 'b'].map((side) => (
            <select key={side} value={col[side] || ''} onChange={(e) => onChange({ [side]: e.target.value })} style={{ ...small, flex: '1 1 45%' }}>
              <option value="">{side === 'a' ? 'A (from)…' : 'B (minus)…'}</option>
              {others.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          ))}
        </div>
      )}
      {k === 'divide' && (
        <div style={row}>
          {['a', 'b'].map((side) => (
            <select key={side} value={col[side] || ''} onChange={(e) => onChange({ [side]: e.target.value })} style={{ ...small, flex: '1 1 45%' }}>
              <option value="">{side === 'a' ? 'A (the part)…' : 'B (out of)…'}</option>
              {dividends.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          ))}
          <input type="number" min="0" max="6" value={f && f.decimals != null ? f.decimals : ''}
            onChange={(e) => onChange({ format: e.target.value === '' ? null : { prefix: '', thousands: ',', ...(f || {}), decimals: Number(e.target.value) } })}
            placeholder="1 dp" title="Decimals in the percent (blank = 1)" style={{ ...small, width: 56 }} />
        </div>
      )}
      {['number', 'diff', 'formula'].includes(k) && (
        <div style={row}>
          <label style={{ ...S.tiny, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={!!f} onChange={(e) => onChange({ format: e.target.checked ? { prefix: '', decimals: 2, thousands: ' ' } : null })} /> Format
          </label>
          {f && (<>
            <input value={f.prefix} onChange={(e) => setF({ prefix: e.target.value })} placeholder="Prefix" title="Before the figure, e.g. “R ”" style={{ ...small, width: 54 }} />
            <input type="number" min="0" max="6" value={f.decimals == null ? '' : f.decimals} onChange={(e) => setF({ decimals: e.target.value === '' ? null : Number(e.target.value) })} placeholder="dp" title="Decimals (blank = as needed)" style={{ ...small, width: 46 }} />
            <select value={f.thousands} onChange={(e) => setF({ thousands: e.target.value })} title="Thousands separator" style={{ ...small, width: 72 }}>
              <option value=",">1,000</option><option value=" ">1 000</option><option value="">1000</option>
            </select>
          </>)}
        </div>
      )}
      <div style={row}>
        <span style={lab}>Colour bands — “20 green, 30 blue, red” (below 20 green … the rest red)</span>
        <Txt value={bandsText(col.bands)} placeholder={`colours: ${BAND_COLORS.join(', ')}`} onCommit={(v) => onChange({ bands: parseBands(v) })} />
        {col.bands && col.bands.length ? (
          <select value={col.bandAs || 'chip'} onChange={(e) => onChange({ bandAs: e.target.value })} style={{ ...small, width: 110 }}>
            <option value="chip">As a chip</option><option value="text">As coloured text</option>
          </select>
        ) : null}
      </div>
    </div>
  );
}

// ---- a Stock check column ----------------------------------------------------------
// Which column says what is IN STOCK (an External database column, normally
// one that adds up every matching row), which says what is NEEDED (a number —
// a part line's Quantity), and which work types it judges (a table line's
// name, e.g. Part; blank = every row). See ./stock.js for how it nets.
function StockBlock({ col, cols, onChange, S, ui, small }) {
  const lab = { ...S.tiny, display: 'block', width: '100%', margin: '2px 0 1px' };
  const sel = { ...small, width: '100%', marginBottom: 4 };
  const others = (cols || []).filter((c) => c.id !== col.id);
  const stockish = others.filter((c) => ['lookup', 'number', 'formula'].includes(c.kind));
  const needish = others.filter((c) => ['number', 'formula', 'lookup'].includes(c.kind));
  const A = others.find((c) => c.id === col.a);
  return (
    <div style={{ marginBottom: 6, borderTop: `1px dashed ${ui.border}`, paddingTop: 6 }}>
      <span style={lab}>In stock — from the column</span>
      <select value={col.a || ''} onChange={(e) => onChange({ a: e.target.value })} style={sel}>
        <option value="">Choose the column with the stock…</option>
        {stockish.map((c) => <option key={c.id} value={c.id}>{c.label}{c.kind === 'lookup' ? ' (External database)' : ''}</option>)}
      </select>
      {A && A.kind === 'lookup' && !(A.lookup && A.lookup.sum) ? (
        <div style={{ ...S.tiny, color: ui.amber, marginBottom: 4 }}>“{A.label}” shows only the FIRST matching row — tick “Add up every matching row” on it to count every storage location.</div>
      ) : null}
      <span style={lab}>Needed — from the column</span>
      <select value={col.b || ''} onChange={(e) => onChange({ b: e.target.value })} style={sel}>
        <option value="">Choose the column with the quantity needed…</option>
        {needish.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <span style={lab}>Judge only these work types (blank = every row)</span>
      <input defaultValue={(col.types || []).join(', ')} key={(col.types || []).join(',')} placeholder="e.g. Part"
        onBlur={(e) => onChange({ types: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={sel} />
      <label style={{ ...S.tiny, display: 'flex', gap: 5, alignItems: 'flex-start', margin: '2px 0 6px' }}>
        <input type="checkbox" checked={!!col.judgeMade} onChange={(e) => onChange({ judgeMade: e.target.checked })} style={{ marginTop: 1 }} />
        <span><strong>Judge parts the grid makes</strong> — off: a part that a work item here makes (its value of the In stock column&rsquo;s field, e.g. Part Number) reads <em>Made</em> and takes no stock.</span>
      </label>
      <div style={{ ...S.tiny, opacity: 0.85 }}>
        Per part, the rows are taken in Due date order — each takes what it needs from what is left, and a row
        reads <strong>Short N</strong> when the stock runs out before it. Done work needs nothing. Every row above
        reads how many short rows are under it.
      </div>
    </div>
  );
}

// ---- an External database column's source --------------------------------------
// Which External Database Source (Settings → Automation), which of its
// tables, which of its columns holds the values to match the work item's
// field with, and which column to show. The pickers list what the signed-in
// admin may read, read from the sources' own catalogue.
let catalogue = null; // one read per page — the sources change rarely
function LookupBlock({ col, onChange, S, ui, small }) {
  const lk = col.lookup || { source: '', table: '', match: '', value: '' };
  const [sources, setSources] = useState(catalogue);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (catalogue) return;
    let on = true;
    externalSources.list()
      .then((list) => { catalogue = list || []; if (on) setSources(catalogue); })
      .catch((e) => { if (on) setError((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'The sources could not be read'); });
    return () => { on = false; };
  }, []);
  const src = (sources || []).find((s) => s.key === lk.source) || null;
  const tables = src ? src.tables : [];
  const table = tables.find((t) => t.name === lk.table) || (tables.length === 1 ? tables[0] : null);
  const columns = table ? table.columns || [] : [];
  const set = (patch) => {
    const next = { ...lk, ...patch };
    const out = { lookup: next };
    // a heading still at the kind's name takes the name of the column shown
    if (patch.value && (!col.label || col.label === 'External database')) out.label = patch.value;
    onChange(out);
  };
  const lab = { ...S.tiny, display: 'block', width: '100%', margin: '2px 0 1px' };
  const sel = { ...small, width: '100%', marginBottom: 4 };
  return (
    <div style={{ marginBottom: 6, borderTop: `1px dashed ${ui.border}`, paddingTop: 6 }}>
      {error ? <div style={{ ...S.tiny, color: ui.red, marginBottom: 4 }}>{error}</div> : null}
      {!error && sources && !sources.length ? (
        <div style={{ ...S.tiny, marginBottom: 4 }}>No External Database Source yet — a site admin connects one in Settings → Automation → External Database Sources.</div>
      ) : null}
      <span style={lab}>Source</span>
      <select value={lk.source} onChange={(e) => set({ source: e.target.value, table: '', match: '', value: '' })} style={sel} disabled={!sources}>
        <option value="">{sources ? 'Choose a source…' : 'Reading the sources…'}</option>
        {(sources || []).map((s) => <option key={s.key} value={s.key}>{s.name}{s.status === 'inactive' ? ' (inactive)' : s.status === 'error' ? ' (needs attention)' : s.status === 'new' ? ' (not connected)' : ''}</option>)}
        {lk.source && !src && sources ? <option value={lk.source}>{lk.source} (not found)</option> : null}
      </select>
      {src && tables.length > 1 ? (
        <>
          <span style={lab}>Table</span>
          <select value={lk.table} onChange={(e) => set({ table: e.target.value, match: '', value: '' })} style={sel}>
            <option value="">Choose a table…</option>
            {tables.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.rows} rows)</option>)}
          </select>
        </>
      ) : null}
      {table ? (
        <>
          <span style={lab}>Its column that holds the field&rsquo;s values</span>
          <select value={lk.match} onChange={(e) => set({ match: e.target.value, ...(tables.length === 1 ? { table: table.name } : {}) })} style={sel}>
            <option value="">Choose the column to match…</option>
            {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <span style={lab}>Show this column</span>
          <select value={lk.value} onChange={(e) => set({ value: e.target.value, ...(tables.length === 1 ? { table: table.name } : {}) })} style={sel}>
            <option value="">Choose the column to show…</option>
            {columns.map((c) => <option key={c.name} value={c.name}>{c.name}{c.type && c.type !== 'text' ? ` · ${c.type}` : ''}</option>)}
          </select>
        </>
      ) : null}
      {table ? (
        <>
          <span style={lab}>When several rows match</span>
          <select value={lk.per ? 'per' : (lk.sum ? 'sum' : 'first')} style={sel}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'first') set({ sum: false, per: '' });
              else if (v === 'sum') set({ sum: true, per: '' });
              else set({ sum: false, per: lk.per || (columns.find((c) => /^(loca|sloc|storage)/i.test(c.name)) || columns[0] || {}).name || '' });
            }}>
            <option value="first">Show the first matching row</option>
            <option value="sum">Add up every matching row (e.g. stock in every storage location)</option>
            <option value="per">Add up per value of a column (e.g. per storage location)</option>
          </select>
          {lk.per ? (
            <>
              <span style={lab}>Per value of its column</span>
              <select value={lk.per} onChange={(e) => set({ per: e.target.value })} style={sel}>
                {columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </>
          ) : null}
        </>
      ) : null}
      <div style={{ ...S.tiny, opacity: 0.85 }}>{lk.per
        ? `Each row shows the shown column added up per ${lk.per} — “1000: 1,126 · 2700: 50.7”; a ${lk.per} holding nothing is left out. ↻ reads the source again.`
        : (lk.sum
          ? 'Each row shows the total of that column over every row of the source whose column equals the work item’s field; no matching row reads 0. ↻ reads the source again.'
          : 'Each row shows the value from the source’s first row whose column equals the work item’s field — letters and spaces matched loosely. ↻ reads the source again.')}</div>
    </div>
  );
}

// ---- one column's own menu ---------------------------------------------------
// TWO MENUS IN ONE, because there are two kinds of answer to "change this
// column". What it IS — its field, its kind, whether it sums — belongs to the
// page and only an admin may say. Whether THIS reader wants to look at it
// does not, and never did: that is why every heading opens this now, and why
// a reader who is not an admin still gets the half that is theirs.
export function ColumnMenu({
  col, index, total, cols, fieldList, ui, S,
  onChange, onRemove, onMove, onClose, anchorRef,
  admin = false, mine = false, onHideForMe = null, pinned = false, onPin = null, narrow = false,
}) {
  const kind = COLUMN_KINDS.find((k) => k.id === col.kind) || {};
  const needsField = !NO_FIELD.includes(col.kind);
  const row = { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' };
  const small = { ...S.input, padding: '4px 7px', fontSize: 12 };
  const btn = { ...S.exportBtn, padding: '2px 8px', fontSize: 11 };
  // A structure has exactly one tree column; losing it turns the page into
  // a flat list, so it cannot be removed and its kind cannot be changed.
  const isTree = col.kind === 'summary';

  const rowBtn = {
    display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
    padding: '5px 7px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: ui.fg, fontFamily: FONT,
  };

  // The half that is always the reader's.
  const mineBlock = (
    <>
      <div style={{ ...S.tiny, fontWeight: 700, marginBottom: 4 }}>
        {mine ? 'Your column' : col.label}
      </div>
      {mine ? (
        <div style={{ ...S.tiny, marginBottom: 6 }}>
          You added this one — it is on your screen and nobody else&rsquo;s.
        </div>
      ) : null}
      {onHideForMe ? (
        <button onClick={onHideForMe} style={{ ...rowBtn, color: mine ? ui.red : ui.fg }}
          onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
          {mine ? '✕ Remove this column' : '✕ Hide this column — for me only'}
        </button>
      ) : null}
      {/* A PIN IS NOT A REFUSAL WITHOUT A REASON. A column somebody cannot
          hide should say who decided that and why, or it reads as a bug. */}
      {pinned && !mine ? (
        <div style={{ ...S.tiny, padding: '4px 7px', color: ui.amber }}>
          📌 Pinned by an admin — this column is what the page is for, so it
          stays. Its width is still yours.
        </div>
      ) : null}
      <div style={{ ...S.tiny, padding: '2px 7px 0' }}>Drag the heading&rsquo;s right edge to resize it. That width is yours too.</div>
      {admin ? <div style={{ borderTop: `1px solid ${ui.border}`, margin: '8px 0 6px' }} /> : null}
      {admin ? <div style={{ ...S.tiny, fontWeight: 700, color: ui.amber, marginBottom: 4 }}>FOR EVERYONE — admin</div> : null}
      {admin && onPin ? (
        <button onClick={onPin} style={{ ...rowBtn, fontWeight: 700 }}
          title={pinned
            ? 'Let readers hide this column again'
            : 'Keep this column on every reader’s screen — they can resize it, but not put it away'}
          onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
          {pinned ? '📌 Pinned — click to unpin' : '📌 Pin this column for everyone'}
        </button>
      ) : null}
    </>
  );

  if (!admin) {
    return (
      <Sheet anchorRef={anchorRef} onClose={onClose} ui={ui} width={272} narrow={narrow} title={col.label || 'Column'}>
        {mineBlock}
      </Sheet>
    );
  }

  return (
    <Sheet anchorRef={anchorRef} onClose={onClose} ui={ui} width={272} narrow={narrow} title={col.label || 'Column'}>
      {mineBlock}
      <div style={{ ...S.tiny, fontWeight: 700, marginBottom: 6 }}>{col.label || 'Column'}</div>

      <div style={row}>
        <input value={col.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Heading" style={{ ...small, flex: '1 1 100%' }} />
      </div>

      {!isTree && (
        <div style={row}>
          <select value={col.kind} onChange={(e) => onChange({ kind: e.target.value })} style={{ ...small, flex: '1 1 100%' }}>
            {COLUMN_KINDS.filter((k) => k.id !== 'summary').map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
      )}

      {needsField && (
        <div style={row}>
          {col.kind === 'lookup' ? <span style={{ ...S.tiny, display: 'block', width: '100%' }}>The work item field to look up</span> : null}
          <input value={col.field} list="wip-fields" onChange={(e) => onChange({ field: e.target.value })}
            placeholder={col.kind === 'lookup' ? 'e.g. Key, Part Number' : 'Field name'} style={{ ...small, flex: '1 1 100%' }} />
        </div>
      )}

      {col.kind === 'lookup' && <LookupBlock col={col} onChange={onChange} S={S} ui={ui} small={small} />}
      {col.kind === 'stock' && <StockBlock col={col} cols={cols} onChange={onChange} S={S} ui={ui} small={small} />}

      {col.kind === 'parent' && (
        <div style={row}>
          <span style={{ ...S.tiny, display: 'block', width: '100%' }}>
            The work item above each row, by name — its icon and summary; a click opens it.
          </span>
          <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}
            title="A sub-task's parent is a task, not the epic — leave those cells blank">
            <input type="checkbox" checked={!!col.skipSubtasks} onChange={(e) => onChange({ skipSubtasks: e.target.checked })} />
            Blank on sub-tasks
          </label>
        </div>
      )}

      {['date', 'number', 'text'].includes(col.kind) && (
        <div style={row}>
          <label style={{ ...S.tiny, display: 'inline-flex', gap: 5, alignItems: 'center' }}
            title="Nobody types into this column on the grid — the work item's own screen still changes it (a date column: only a bar drag on the timeline moves it)">
            <input type="checkbox" checked={!!col.readOnly} onChange={(e) => onChange({ readOnly: e.target.checked })} />
            Read only on the grid
          </label>
        </div>
      )}

      {col.kind === 'formula' && (
        <div style={{ marginBottom: 6 }}>
          {/* Commits on blur, so a half-typed expression is not run on every key. */}
          <textarea defaultValue={col.formula || ''} key={col.id} rows={3} spellCheck={false}
            onBlur={(e) => { if (e.target.value !== (col.formula || '')) onChange({ formula: e.target.value }); }}
            placeholder={'number("Estimated Hours") - number("Actual Hours")\n// or: date("Planned End") > date("duedate") ? "Late" : ""'}
            style={{ ...small, width: '100%', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', resize: 'vertical', lineHeight: 1.4 }} />
          <details style={{ ...S.tiny }}>
            <summary style={{ cursor: 'pointer' }}>What a formula can use</summary>
            {FORMULA_HELP.filter(([sig]) => !sig.startsWith('bucket')).map(([sig, what]) => <div key={sig}><code style={{ color: ui.fg }}>{sig}</code> — {what}</div>)}
            <div>A number, text, a date or true (✓) — an error shows #ERR with the reason on hover. Sum (Σ) adds the numbers up over the work items under a row. Count (#) flags: a work item shows its own answer, a folder or a group how many work items under it have one (anything but blank, 0 or false) — a colour band such as “red” paints both.</div>
          </details>
        </div>
      )}

      <div style={row}>
        <label style={{ ...S.tiny, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          Width
          <input type="number" min="40" max="600" step="4" value={col.width}
            onChange={(e) => onChange({ width: Number(e.target.value) })} style={{ ...small, width: 68 }} />
        </label>
        <select value={col.align} onChange={(e) => onChange({ align: e.target.value })} style={{ ...small, width: 84 }}>
          <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option>
        </select>
        {kind.agg ? (
          <select value={col.agg} onChange={(e) => onChange({ agg: e.target.value })} style={{ ...small, width: 92 }}>
            <option value="none">Own value</option>
            {col.kind === 'number' || col.kind === 'formula' ? <option value="sum">Sum (Σ)</option> : null}
            {col.kind === 'formula' ? <option value="count">Count (#)</option> : null}
            {col.kind === 'date' ? <option value="span">Span</option> : null}
          </select>
        ) : null}
      </div>

      {kind.hint ? <div style={{ ...S.tiny, opacity: 0.85, marginBottom: 6 }}>{kind.hint}</div> : null}

      <ShapeBlock col={col} cols={cols} onChange={onChange} S={{ ...S, ui }} small={small} row={row} />

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', borderTop: `1px solid ${ui.border}`, paddingTop: 6 }}>
        <button onClick={() => onMove(-1)} disabled={index === 0} style={{ ...btn, opacity: index === 0 ? 0.4 : 1 }}>← Move</button>
        <button onClick={() => onMove(1)} disabled={index === total - 1} style={{ ...btn, opacity: index === total - 1 ? 0.4 : 1 }}>Move →</button>
        <button onClick={() => onChange({ on: false })} style={{ ...btn, marginLeft: 'auto' }} title="Keep the column in the config but take it off the grid">Hide</button>
        {!isTree && <button onClick={onRemove} style={{ ...btn, color: ui.red }}>Remove</button>}
      </div>
      {isTree ? <div style={{ ...S.tiny, marginTop: 6, opacity: 0.85 }}>This is the tree — the structure keeps exactly one, so it cannot be removed.</div> : null}
    </Sheet>
  );
}

// ---- the "+" at the end of the heading row -----------------------------------
// THE "+" IS EVERYBODY'S. A reader adds a column to their own view; an admin
// can also add one to the page. It is one menu because it is one question —
// "show me this field" — and the only difference is who ends up seeing it.
export function AddColumn({
  cols, hidden, fieldList, ui, S, onAdd, onShow,
  canShare = false, mineCount = 0, hiddenForMe = [], onAddMine, onShowForMe, onReset, narrow = false,
}) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btn = { ...S.exportBtn, padding: '2px 8px', fontSize: 11 };

  // Fields worth offering first: the ones this site actually has, plus the
  // structural pseudo-fields, minus what is already on the grid.
  const used = useMemo(() => new Set(cols.map((c) => String(c.field || '').toLowerCase()).filter(Boolean)), [cols]);
  const options = useMemo(() => {
    const term = q.trim().toLowerCase();
    const pseudo = PSEUDO_FIELDS.map((p) => ({ name: p.label, kind: 'text', pseudo: true }));
    const real = (fieldList || []).map((f) => ({ name: f.name, kind: guessKind(f), id: f.id || f.key }));
    return [...pseudo, ...real]
      .filter((f) => f.name && !used.has(String(f.name).toLowerCase()))
      .filter((f) => !term || String(f.name).toLowerCase().includes(term))
      .slice(0, 60);
  }, [fieldList, used, q]);

  return (
    <span ref={anchor} style={{ display: 'inline-flex', alignItems: 'center', height: '100%', paddingLeft: 6 }}>
      <button onClick={() => { setOpen((o) => !o); setQ(''); }}
        title={mineCount ? `Add a column — you have ${mineCount} of your own` : 'Add a column to your view'}
        style={{
          background: 'none', border: `1px dashed ${ui.border}`, borderRadius: 6, color: ui.muted,
          cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '2px 8px', fontFamily: FONT, fontWeight: 700,
        }}>+</button>
      {open && (
        <Sheet anchorRef={anchor} onClose={() => setOpen(false)} ui={ui} width={280} narrow={narrow} title="Add a column">
          <div style={{ ...S.tiny, fontWeight: 700, marginBottom: 2 }}>Add a column</div>
          <div style={{ ...S.tiny, marginBottom: 6 }}>
            {canShare
              ? 'It goes on YOUR screen. Hold Shift when you pick one to put it on the page for everybody.'
              : 'It goes on your screen, and nobody else’s. Its heading is underlined so you can tell.'}
          </div>
          {/* NO KEYBOARD AMBUSH. autoFocus on a phone throws the on-screen
              keyboard up over the list the reader came to look at. */}
          <input autoFocus={!narrow} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a field…"
            style={{ ...S.input, padding: '4px 7px', fontSize: 12, marginBottom: 6 }} />

          {/* Something this reader put away, offered back before the whole
              field list — it is the likeliest thing they are looking for. */}
          {(hiddenForMe || []).length ? (
            <>
              <div style={{ ...S.tiny, opacity: 0.8, margin: '4px 0 3px' }}>You hid these</div>
              {(hiddenForMe || []).map((id) => (
                <button key={id} onClick={() => { onShowForMe(id); setOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '4px 7px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: ui.fg, fontFamily: FONT }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                  ↩ {(cols.find((c) => c.id === id) || {}).label || id}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${ui.border}`, margin: '6px 0' }} />
            </>
          ) : null}

          {hidden.length > 0 && onShow ? (
            <>
              <div style={{ ...S.tiny, opacity: 0.8, margin: '4px 0 3px' }}>Hidden, put it back</div>
              {hidden.map((c) => (
                <button key={c.id} onClick={() => { onShow(c.id); setOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '4px 7px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: ui.fg, fontFamily: FONT }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                  {c.label}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${ui.border}`, margin: '6px 0' }} />
            </>
          ) : null}

          <div style={{ ...S.tiny, opacity: 0.8, margin: '2px 0 3px' }}>A field on this site</div>
          {options.length === 0 ? <div style={{ ...S.tiny, padding: '4px 7px' }}>Nothing matches.</div> : null}
          {options.map((f) => (
            <button key={`${f.name}-${f.id || 'p'}`}
              onClick={(e) => { if (canShare && e.shiftKey && onAdd) onAdd(f); else onAddMine(f); setOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '4px 7px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: ui.fg, fontFamily: FONT }}
              onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
              {f.name}
              <span style={{ ...S.tiny, marginLeft: 6 }}>{f.pseudo ? 'structure' : f.kind}</span>
            </button>
          ))}

          <div style={{ borderTop: `1px solid ${ui.border}`, marginTop: 6, paddingTop: 6, display: 'flex', gap: 6 }}>
            {[['count', 'Count'], ['countSub', 'Sub-Items'], ['progress', 'Progress']].map(([kind, label]) => (
              <button key={kind} style={btn}
                onClick={(e) => {
                  const f = { name: '', kind, label: kind === 'countSub' ? 'Count Sub-Items' : label };
                  if (canShare && e.shiftKey && onAdd) onAdd(f); else onAddMine(f);
                  setOpen(false);
                }}>{label}</button>
            ))}
          </div>
          {onReset ? (
            <div style={{ borderTop: `1px solid ${ui.border}`, marginTop: 6, paddingTop: 6 }}>
              <button onClick={() => { onReset(); setOpen(false); }}
                style={{ ...btn, color: ui.muted, width: '100%' }}
                title="Forget the columns you added, the ones you hid and the widths you set">
                ↺ Back to the page&rsquo;s own columns
              </button>
            </div>
          ) : null}
        </Sheet>
      )}
    </span>
  );
}

// A field's shape decides the column kind it wants. The admin can change it
// in the menu; this only has to be right often enough to save a click.
function guessKind(f) {
  const t = String((f && (f.schemaType || f.type)) || '').toLowerCase();
  const items = String((f && (f.schemaItems || f.items)) || '').toLowerCase();
  if (t === 'date' || t === 'datetime') return 'date';
  if (t === 'number') return 'number';
  if (t === 'array' && items === 'string') return 'text';
  return 'text';
}

export const columnFor = (f) => {
  if (f.kind === 'count' || f.kind === 'countSub' || f.kind === 'progress') {
    return newColumn({ kind: f.kind, label: f.label, field: '', width: f.kind === 'countSub' ? 110 : 76, align: 'right' });
  }
  const kind = f.kind || 'text';
  return newColumn({
    kind,
    label: f.name,
    field: f.name,
    width: kind === 'date' ? 96 : (kind === 'number' ? 118 : 150),
    align: kind === 'number' ? 'right' : 'left',
  });
};
